// =====================================================================
// src/lib/garagem.js — as decisões da garagem, sem DOM
//
// A garagem tem TRÊS telas, não uma, e elas quase não se parecem:
//
//   guarita       um campo de placa e uma resposta enorme. Nada mais.
//   imobiliaria   as vagas DELA e as placas DELA, e os dois botões de
//                 declarar. Ela nunca vê o prédio. (Até a 0100 esta era a
//                 tela do PROPRIETÁRIO; na 0101 a vaga e a placa passaram a
//                 ser da imobiliária — o dono não tem login, Hugo 02/11.)
//   balcão        o mapa, as reservas e as liberações.
//
// Quem escolhe é `visaoDe()`, e a escolha é do BANCO, não da tela: o
// contexto diz se a pessoa é de imobiliária e qual o nível dela no módulo.
// Tela que decide permissão sozinha é tela que um dia decide errado.
//
// O que está aqui é o que não precisa de navegador — e por isso é provado
// em node, não clicando.
// =====================================================================

// ── QUEM VÊ O QUÊ ────────────────────────────────────────────────────
// A imobiliária vem PRIMEIRO, pelo módulo `vaga_garagem` (nível
// imobiliária): é a aba Garagem dela (0101). O hotel (login sem
// imobiliária) continua com o módulo `garagem`: editar é o balcão, ver é a
// guarita. Uma imobiliária sem `vaga_garagem` não tem tela — o `garagem`
// é do hotel, e a semente não o dá ao papel `imobiliaria`.
export function visaoDe(contexto) {
  const c = contexto || {};
  const modulos = Array.isArray(c.modulos) ? c.modulos : [];
  const nivelDe = ch => (modulos.find(x => x && x.chave === ch) || {}).nivel;
  if (c.imobiliaria && c.imobiliaria.id) {
    return ['ver', 'editar'].includes(nivelDe('vaga_garagem')) ? 'imobiliaria' : null;
  }
  const n = nivelDe('garagem');
  if (n !== 'ver' && n !== 'editar') return null;   // sem o módulo, não há tela
  return n === 'editar' ? 'balcao' : 'guarita';
}

// Declarar vaga e placa: a imobiliária com `editar vaga_garagem` — o mesmo
// que `declarar_vaga`/`declarar_placa` cobram na 0101. Com `ver`, ela olha
// as vagas e as placas dela e não ganha botão que o banco recusaria.
export function podeDeclarar(contexto) {
  if (visaoDe(contexto) !== 'imobiliaria') return false;
  const m = (contexto.modulos || []).find(x => x && x.chave === 'vaga_garagem');
  return !!m && m.nivel === 'editar';
}

// A frase do topo da tela da imobiliária (esboço da 0099, parte 1).
export function textoDaImobiliaria(contexto) {
  const nome = contexto && contexto.imobiliaria && contexto.imobiliaria.nome;
  return `As vagas e os carros da ${nome || 'imobiliária'}. Quem libera é o hotel.`;
}

// A legenda debaixo do título (0047 / Hugo "4 A"): a guarita não "lê", ela
// consulta e abre o portão. Nulo deixa a regra padrão (o nível) falar.
export function legendaDe(contexto) {
  const v = visaoDe(contexto);
  if (v === 'guarita') return 'Consulta de placa';
  if (v === 'imobiliaria') return 'As vagas e as placas da imobiliária';
  return null;
}

// ── O MAPA ───────────────────────────────────────────────────────────
// `vagas_do_cliente()` (0037) já devolve ordenado por setor e ordem. Aqui
// só se agrupa: a ordem do banco é preservada dentro de cada faixa, e a
// faixa sem setor vai para o fim com nome próprio — some do mapa seria
// pior, porque some justamente a vaga que ninguém cadastrou direito.
export const SEM_SETOR = 'Sem setor';

export function agruparPorSetor(vagas) {
  const faixas = [];
  const indice = new Map();
  for (const v of vagas || []) {
    const chave = (v && v.setor) ? v.setor : SEM_SETOR;
    if (!indice.has(chave)) { indice.set(chave, { setor: chave, vagas: [] }); faixas.push(indice.get(chave)); }
    indice.get(chave).vagas.push(v);
  }
  // O "Sem setor" para o fim, aconteça o que acontecer com a ordem que veio.
  faixas.sort((a, b) => (a.setor === SEM_SETOR) - (b.setor === SEM_SETOR));
  return faixas;
}

// ── A COR DE CADA VAGA ───────────────────────────────────────────────
// Quatro estados, e nenhum deles é "reservada": a vaga RESERVADA mas sem
// check-in continua LIVRE no mapa, porque é isso que a guarita vê no
// portão (0034 — sem `liberada_em`, a placa não chegou). Mapa que mostra
// ocupação que o portão não reconhece é pior do que mapa nenhum.
export function estadoDaVaga(v) {
  if (!v) return { chave: 'livre', rotulo: 'Livre', cor: '#16a34a' };
  if (v.situacao === 'pendente') return { chave: 'pendente', rotulo: 'Aguardando liberação', cor: '#d97706' };
  if (v.situacao === 'inativa')  return { chave: 'inativa',  rotulo: 'Inativa',              cor: '#6b7280' };
  if (v.ocupada_hoje)            return { chave: 'ocupada',  rotulo: 'Ocupada hoje',         cor: '#dc2626' };
  return { chave: 'livre', rotulo: 'Livre', cor: '#16a34a' };
}

export function resumoDoMapa(vagas) {
  const c = { livre: 0, ocupada: 0, pendente: 0, inativa: 0 };
  for (const v of vagas || []) c[estadoDaVaga(v).chave]++;
  return c;
}

// ── A PLACA, DO LADO DE CÁ ───────────────────────────────────────────
// Espelho de `app.placa()` (0032): maiúscula, sem espaço nem hífen. NÃO
// é validação — quem valida é o banco. Isto existe para a guarita ver o
// que digitou do jeito que o sistema vai procurar, e não para impedir
// nada: se a tela recusasse o que o banco aceita, a guarita ficaria com
// o carro na rampa achando que o sistema quebrou.
export function normalizarPlaca(p) {
  return String(p == null ? '' : p).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

// Sete caracteres é o padrão brasileiro, antigo e Mercosul. Serve para
// ACENDER o botão, não para bloquear: o `disabled` some com 7, e digitar
// 6 e apertar Enter continua mandando — o banco que responda.
export function placaParecePronta(p) {
  return normalizarPlaca(p).length >= 7;
}

// ── O PERÍODO, ANTES DE MANDAR ───────────────────────────────────────
// A 0032 recusa período invertido e sobreposição no banco, e é essa a
// proteção. Isto é só para não fazer a recepcionista digitar tudo e levar
// um erro depois — a recusa cara de que fala o comentário da 0037.
export function validarPeriodo(de, ate) {
  if (!de || !ate) return 'Informe as duas datas';
  if (String(ate) < String(de)) return 'A saída não pode ser antes da entrada';
  return null;
}

// Sobreposição: dois períodos se cruzam quando um começa antes de o outro
// terminar, dos dois lados. Aqui as pontas CONTAM — `de` e `ate` são dias
// inteiros de ocupação da vaga, não horários de entrada e saída, então
// quem sai dia 10 e quem entra dia 10 disputam a mesma vaga.
export function conflitaCom(reservas, de, ate) {
  if (!de || !ate) return [];
  return (reservas || []).filter(r =>
    r && r.situacao === 'confirmada' && String(r.de) <= String(ate) && String(r.ate) >= String(de));
}

// ── A RESPOSTA DA GUARITA ────────────────────────────────────────────
// Ela precisa ser lida de relance, com o carro parado na rampa. Verde ou
// vermelho, e uma frase. O motivo vem do banco quando existe (0032/0034):
// "não" sem motivo na guarita vira ligação para a recepção.
export function respostaDaGuarita(r) {
  if (!r) return { autorizado: false, titulo: 'Sem resposta', detalhe: 'Tente de novo', cor: '#6b7280' };
  if (r.autorizado) {
    // 0045: na VISITA, `vaga` não é vaga — é "Fornecedor · destino ·
    // janela", a instrução para o porteiro. "Vaga Zé Eletricista · 812"
    // seria mentira em letra grande.
    const detalhe = r.tipo === 'visita' ? (r.vaga || 'Visita autorizada')
                  : r.vaga ? `Vaga ${r.vaga}` : 'Vaga liberada';
    return {
      autorizado: true,
      titulo: 'PODE ENTRAR',
      detalhe,
      ate: r.ate || null,
      tipo: r.tipo || null,
      cor: '#16a34a',
    };
  }
  return {
    autorizado: false,
    titulo: 'NÃO ABRIR',
    detalhe: r.motivo || 'Placa sem autorização',
    cor: '#dc2626',
  };
}

// ── O QUADRO: VAGA × DIA (Hugo, 23/09/2026) ──────────────────────────
// "quero que a garagem tenha a opção de visualização por quadro,
// semelhante às reservas, com o número da vaga e cores — verde
// disponível, amarela reservada, vermelho ocupada". E, perguntado o que
// separa amarelo de vermelho: "amarelo reservado, mas não houve check-in
// do hóspede; vermelho fez check-in — confirmou a reserva e a placa pela
// recepção; verde, sem previsão de reserva de garagem na data".
//
// O "check-in" da vaga é `liberada_em` (0032, `liberar_garagem`):
// é o instante em que a recepção confere a placa e a guarita passa a
// abrir. Por isso a reserva inteira fica VERMELHA a partir dele — todos
// os dias dela, não só os que já passaram: o carro está no prédio.
//
// O mapa (`estadoDaVaga`) continua dizendo "livre" para a reservada sem
// check-in: ele responde "o que o portão faz AGORA". O quadro responde
// outra pergunta — "o que está previsto para cada dia" — e por isso tem o
// amarelo que o mapa não tem. As duas coisas são verdade ao mesmo tempo.
//
// Vaga aguardando liberação ou inativa não aceita reserva (0032), e entra
// no quadro em CINZA (Hugo, "entram em cinza"): o balcão vê o prédio
// inteiro, como no mapa.
export const CORES_DO_QUADRO = Object.freeze({
  livre:        { fundo: '#dcfce7', texto: '#166534', rotulo: 'Disponível' },
  reservada:    { fundo: '#facc15', texto: '#713f12', rotulo: 'Reservada' },
  ocupada:      { fundo: '#dc2626', texto: '#ffffff', rotulo: 'Ocupada' },
  indisponivel: { fundo: '#d1d5db', texto: '#4b5563', rotulo: 'Indisponível' },
});
export const LEGENDA_DO_QUADRO = Object.freeze(['livre', 'reservada', 'ocupada', 'indisponivel']
  .map(k => Object.freeze({ chave: k, rotulo: CORES_DO_QUADRO[k].rotulo, cor: CORES_DO_QUADRO[k].fundo })));

// O período da vaga é FECHADO nas duas pontas: `de <= dia <= ate`, como o
// banco confere (0032 `reservar_vaga`, 0034 a guarita). Cancelada não pinta.
export function reservaDaVagaNoDia(reservas, vagaId, iso) {
  return (reservas || []).find(r => r && r.vaga_id === vagaId && r.situacao !== 'cancelada'
    && r.de <= iso && r.ate >= iso) || null;
}

export function estadoDaVagaNoDia(vaga, reservas, iso) {
  if (!vaga || vaga.situacao !== 'liberada') {
    return { chave: 'indisponivel', rotulo: vaga && vaga.situacao === 'pendente' ? 'Aguardando liberação' : 'Inativa', res: null };
  }
  const r = reservaDaVagaNoDia(reservas, vaga.id, iso);
  if (!r) return { chave: 'livre', rotulo: 'Disponível', res: null };
  if (r.liberada_em) return { chave: 'ocupada', rotulo: 'Ocupada — check-in feito', res: r };
  return { chave: 'reservada', rotulo: 'Reservada — aguarda check-in', res: r };
}

// A linha inteira de uma vaga, dia a dia, e se a célula abre o nome (só
// no primeiro dia da reserva que aparece na janela — como no quadro de
// reservas, 4985).
export function linhaDaVaga(vaga, reservas, dias) {
  const vistas = new Set();
  return (dias || []).map(iso => {
    const est = estadoDaVagaNoDia(vaga, reservas, iso);
    const mostra = !!est.res && !vistas.has(est.res.id);
    if (est.res) vistas.add(est.res.id);
    return { iso, ...est, mostra };
  });
}

// ── A VAGA DA RESERVA, NO BALCÃO (0097 — 6A, Hugo 01/11/2026) ────────
// A vaga sai junto com o check-in (1B): no PRIMEIRO check-in da reserva, se
// ela tem exatamente UMA vaga aguardando, a tela pede a placa no mesmo
// diálogo. Placa vazia é o check-in de sempre — a vaga continua aguardando,
// e o cartão Garagem da ficha libera depois. Com duas ou mais vagas a tela
// não adivinha qual: libera-se cada uma no cartão. O banco confere tudo de
// novo (fazer_checkin com p_placa); aqui é só para a pergunta certa
// aparecer na hora certa.
export function vagasAguardando(vagas) {
  return (vagas || []).filter(v => v && !v.liberada_em);
}
export function vagaDoPrimeiroCheckin(reserva, vagas) {
  if (!reserva) return null;
  const alguemDentro = (reserva.gente || []).some(h => h && h.chegada_em && !h.saida_em);
  if (alguemDentro) return null;
  const pend = vagasAguardando(vagas);
  return pend.length === 1 ? pend[0] : null;
}

// O selo da vaga no cartão Garagem e nos Detalhes da Reserva: as mesmas
// duas cores do quadro (amarela aguardando, vermelha liberada), em badge.
export function seloDaVaga(v) {
  if (!v) return null;
  return v.liberada_em
    ? { classe: 'badge-green', texto: `${v.vaga}${v.placa ? ' · ' + v.placa : ''} · liberada` }
    : { classe: 'badge-orange', texto: `${v.vaga} · aguarda check-in` };
}

// O período que o modal "Nova Reserva de Vaga" aceita: o mesmo que
// `reservar_vaga` (0097) — dentro da reserva, fechado nas pontas. A recusa
// cara vem do banco; esta evita digitar tudo para levar o erro depois.
export function periodoDaVagaNaReserva(de, ate, reserva) {
  const e = validarPeriodo(de, ate);
  if (e) return e;
  if (reserva && (String(de) < String(reserva.check_in) || String(ate) > String(reserva.check_out))) {
    return 'O período da vaga tem de caber na reserva';
  }
  return null;
}

// "Quarto 105 — Marina Costa (03/11 → 06/11)": o rótulo do MdM
// (garagemForm, 'Flat X — nome (ci → co)'), com quarto no lugar de flat.
export function rotuloDaReservaParaVaga(r) {
  if (!r) return '';
  const dm = iso => (iso ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}` : '?');
  return `Quarto ${r.quarto || '?'} — ${r.hospede || 'sem hóspede cadastrado'} (${dm(r.check_in)} → ${dm(r.check_out)})`;
}
